"use client";

import { useScroll, useTransform, MotionValue } from "framer-motion";
import { useMemo } from "react";

export interface ColorStop {
  chapterId: string;
  color: string;
  position: number; // scroll position in pixels
}

/**
 * Custom hook to interpolate background colors based on scroll position
 * Creates smooth crossfade transitions between chapter colors
 */
export function useScrollColor(colorStops: ColorStop[]): MotionValue<string> {
  const { scrollY } = useScroll();

  // Extract positions and colors directly from colorStops
  const scrollPositions = useMemo(
    () => colorStops.map((stop) => stop.position),
    [colorStops]
  );

  const colors = useMemo(
    () => colorStops.map((stop) => stop.color),
    [colorStops]
  );

  // Create the color interpolation using useTransform
  // Use fallback values only if arrays are empty
  const backgroundColor = useTransform(
    scrollY,
    scrollPositions.length > 0 ? scrollPositions : [0, 1000],
    colors.length > 0 ? colors : ["#E4FF97", "#ADA9F9"]
  );

  return backgroundColor;
}

/**
 * Calculate scroll positions for each chapter based on their DOM positions
 * Returns an array of color stops with "hold zones" to keep colors constant
 * within chapters, only transitioning at chapter boundaries
 */
export function calculateColorStops(
  chapterColors: Array<{ id: string; color: string }>
): ColorStop[] {
  const colorStops: ColorStop[] = [];
  const transitionDistance = 200; // Pixels before chapter where transition starts

  chapterColors.forEach(({ id, color }, index) => {
    const element = document.getElementById(id);
    if (element) {
      // Get the position where the chapter starts
      const rect = element.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const elementTop = rect.top + scrollTop;

      if (index === 0) {
        // First chapter: set color from the very beginning
        colorStops.push({
          chapterId: id,
          color: color,
          position: 0,
        });
      } else {
        // For subsequent chapters:
        // 1. Hold the previous color until just before this chapter
        const previousColor = chapterColors[index - 1].color;
        colorStops.push({
          chapterId: `${id}-hold`,
          color: previousColor,
          position: Math.max(0, elementTop - transitionDistance),
        });

        // 2. Transition to new color at the chapter start
        colorStops.push({
          chapterId: id,
          color: color,
          position: elementTop,
        });
      }
    }
  });

  return colorStops;
}
